import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger" | "outline";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-bridge-accent hover:bg-bridge-accentSoft text-white shadow-soft border border-white/10",
  secondary:
    "bg-bridge-surface/80 hover:bg-bridge-panel text-bridge-fg border border-bridge-border/70",
  ghost: "bg-transparent hover:bg-white/5 text-bridge-fg border border-transparent",
  danger: "bg-bridge-danger/90 hover:bg-bridge-danger text-white border border-white/10",
  outline: "bg-transparent hover:bg-bridge-surface text-bridge-fg border border-bridge-border",
};

const SIZES: Record<Size, string> = {
  sm: "h-8 px-3 text-sm rounded-sm",
  md: "h-10 px-4 text-sm rounded-md",
  lg: "h-12 px-5 text-base rounded-md",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 font-medium transition-all focus-ring active:scale-[0.97] motion-reduce:active:scale-100",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading && <span className="h-3 w-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
      {children}
    </button>
  );
});